export const AUTH_ROUTE = "/auth";
export const LOGIN_ROUTE = `${AUTH_ROUTE}/login`;
export const SIGNUP_ROUTE = `${AUTH_ROUTE}/signup`;
export const OTP_ROUTE = `${AUTH_ROUTE}/otp`;
export const RESET_PASSWORD = `${AUTH_ROUTE}/reset-password`;
export const RESET_PASSWORD_UPDATE = `${AUTH_ROUTE}/reset-password/:resetCode`;

export const COACH_DETAILS = "/coach-details";

export const COACH_PANEL = "/coach-panel";
export const DASHBOARD = `${COACH_PANEL}/dashboard`;
export const XRAY = `${COACH_PANEL}/x-ray`;
export const QUEST = `${COACH_PANEL}/goals-and-rhythms`;
export const CONTACTS = `${COACH_PANEL}/contacts`;
export const FEED = `${COACH_PANEL}/feed`;

export const XRAY_SCORE = `${XRAY}/score`;
export const XRAY_USER_SCORE = `${XRAY_SCORE}/:userId`;
export const XRAY_USER_CHAT = `${XRAY_USER_SCORE}/chat/:respondentId`;

export const STRATEGY_CANVAS = `${COACH_PANEL}/strategy-canvas`;
export const STRATEGY_CANVAS_BRIEF = `${STRATEGY_CANVAS}/brief/:briefId`;
export const STRATEGY_CANVAS_REVIEW = `${STRATEGY_CANVAS}/brief/:briefId/review`;
export const STRATEGY_CANVAS_LIVE_REVIEW_WAITING = `${STRATEGY_CANVAS}/brief/:briefId/live-review`;
export const STRATEGY_CANVAS_VIEW = `${STRATEGY_CANVAS}/view/:briefId`;

// Execution Blueprint (EXW) - coach side. There is no coach-facing "Brief"
// screen in this contract (COACH-API.md has no brief endpoint), so unlike
// Strategy Canvas the dashboard goes straight from the client card into
// Review Mode - no intermediate route. Blueprint Mode has no route of its
// own either: it's a mode toggle inside the review screen, not a separate
// URL (see docs/execution-blueprint/PROGRESS.md).
export const EXECUTION_BLUEPRINT = `${COACH_PANEL}/execution-blueprint`;
export const EXECUTION_BLUEPRINT_REVIEW = `${EXECUTION_BLUEPRINT}/:blueprintId/review`;
// Live Review is reachable ONLY by starting it from the review screen - the
// screen itself bounces anyone who lands here without having done that. See
// ExecutionBlueprintLiveReview.js for why that gate is currently local state
// rather than a server check.
export const EXECUTION_BLUEPRINT_LIVE_REVIEW = `${EXECUTION_BLUEPRINT}/:blueprintId/live-review`;
// WIG Connections - stage 2 of the Execution Blueprint journey (the stepper in
// the live-review popup names it). Both routes are registered so the screens
// can be opened directly, but NOTHING links to either one: they're design
// only, with no API or socket behind them, and get wired into the flow when
// asked for. Don't add navigation to them without checking first.
export const EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING = `${EXECUTION_BLUEPRINT}/:blueprintId/wig-connections/building`;
export const EXECUTION_BLUEPRINT_WIG_CONNECTIONS = `${EXECUTION_BLUEPRINT}/:blueprintId/wig-connections`;
export const EXECUTION_BLUEPRINT_YEAR_ONE_PLAN = `${EXECUTION_BLUEPRINT}/:blueprintId/year-one-plan`;
// ── After the terminal lock (§6b.7) ──────────────────────────────────────────
// `blueprint_state === "LOCKED"`. The dashboard sends a coach here instead of
// into the review, and this is where the journey ends: a read-only view of the
// three finished stages, one per tab.
//
// ⚠️ There was a second route here — `EXECUTION_BLUEPRINT_LOCKED`
// (`/:blueprintId/locked`), the "Final Locked Blueprint" hub — DELETED
// 2026-09-02 on instruction along with the screen. It also carried an ordering
// warning: no EXW route uses `exact`, so `/locked` is a strict prefix of
// `/locked/:tab` and would have swallowed it. With the hub gone the remaining
// route has nothing to collide with and no ordering constraint.
export const EXECUTION_BLUEPRINT_LOCKED_VIEW = `${EXECUTION_BLUEPRINT}/:blueprintId/locked/:tab`;

// The tab everything lands on when it has no reason to prefer another — the
// first of the three, and where the deleted hub used to send its first card.
export const EXECUTION_BLUEPRINT_LOCKED_DEFAULT = `${EXECUTION_BLUEPRINT}/:blueprintId/locked/blueprint`;

// Annual OKR - coach side. The workflow downstream of the Execution Blueprint:
// the CEO turns their locked Year 1 WIGs into Annual Objectives, Key Results
// and Initiatives, and the coach reviews them.
//
// Three screens: the client dashboard, the live review, and a read of the OKRs
// themselves. Smaller than EXW, which also has a solo review surface.
//
// ⚠️ The dashboard was originally out of scope and added on 2026-09-20 - see
// D1/D5 in docs/annual-okr/PROGRESS.md. It is what a coach reaches from the
// sidebar, and what makes the other two screens reachable at all.
export const ANNUAL_OKR = `${COACH_PANEL}/annual-okrs`;
// The joint session with the CEO. Symmetric with the CEO app's copy of this
// screen - same room, same events, `viewerRole` the only difference.
export const ANNUAL_OKR_LIVE_REVIEW = `${ANNUAL_OKR}/:annualOkrId/live-review`;
// The coach's async review pass — the screen the dashboard's CTA opens for a
// client at SENT_TO_COACH. Mirrors `EXECUTION_BLUEPRINT_REVIEW`.
//
// ⚠️ REPLACED `ANNUAL_OKR_VIEW` (`/:annualOkrId`), a bare placeholder screen
// that the dashboard button pointed at (2026-09-21). It is gone, not kept
// alongside: a read-only "view" and the review screen are the same screen, and
// the review one is the one with the work in it.
//
// ⚠️ Registered BELOW the live-review route: no route here uses `exact`, so
// `/:annualOkrId/review` and `/:annualOkrId/live-review` are siblings, but the
// bare dashboard route still has to come last. Same trap the EXW block
// documents.
export const ANNUAL_OKR_REVIEW = `${ANNUAL_OKR}/:annualOkrId/review`;

// The finished board, after the terminal lock (§7.5). Both apps land here when
// `annual_okr_locked` broadcasts — the run is over, and the thing either party
// wants next is the result, not the screen that got them there.
//
// ⚠️ Design still to come. The screen is routed and reachable so the URL shape
// and the Switch ordering are settled once; its contents arrive with the
// hand-off.
export const ANNUAL_OKR_VIEW_OKRS = `${ANNUAL_OKR}/:annualOkrId/okrs`;

// ── Quarterly OKRs (AI flow) ─────────────────────────────────────────────────
// Keyed by the QUARTER row id, not the run id: every coach route and email
// link carries `quarter_company_id` (handoff §9, §14). One path per stage; the
// review path also shows the "sent — start the live review" state.
// No route here is `exact`, so the bare dashboard must be registered LAST.
export const QUARTERLY_OKR = `${COACH_PANEL}/quarterly-okrs`;
// The coach brief (handoff 34) — the Strategy Canvas brief's twin: the
// dashboard opens it first while the quarter is with the coach, and its
// header button goes on to the review.
export const QUARTERLY_OKR_BRIEF = `${QUARTERLY_OKR}/:quarterCompanyId/brief`;
export const QUARTERLY_OKR_REVIEW = `${QUARTERLY_OKR}/:quarterCompanyId/review`;
export const QUARTERLY_OKR_LIVE_REVIEW = `${QUARTERLY_OKR}/:quarterCompanyId/live-review`;
export const QUARTERLY_OKR_SUMMARY = `${QUARTERLY_OKR}/:quarterCompanyId/summary`;
export const QUARTERLY_OKR_SCORECARD = `${QUARTERLY_OKR}/:quarterCompanyId/scorecard`;

// ── OKR Plans (coach) ───────────────────────────────────────────────────────
// CoachPanel/OkrPlans/OKR_PLANS_COACH.md §8.2: every route lives under the base.
export const COACH_OKR_PLANS_BASE = `${COACH_PANEL}/okr-plans`;
export const COACH_OKR_PLANS = COACH_OKR_PLANS_BASE;
export const COACH_OKR_PLANS_COMPANY = `${COACH_OKR_PLANS_BASE}/companies/:companyId`;
export const COACH_OKR_PLAN = `${COACH_OKR_PLANS_BASE}/plans/:planUniqueId`;
export const COACH_OKR_PLAN_REPORT = `${COACH_OKR_PLAN}/reports/:reportUniqueId`;
export const COACH_OKR_PLAN_REVIEW = `${COACH_OKR_PLAN}/review`;
export const COACH_OKR_PLAN_CHAT = `${COACH_OKR_PLAN}/chats/:chatUniqueId`;

export const ACADEMY = COACH_PANEL + "/scaling-resources";
export const COURSE_OVERVIEW = ACADEMY + "/course/:courseId";
export const STUDY_COURSE = ACADEMY + "/study-course/:courseId";
export const FEEDBACK_COURSE = ACADEMY + "/feedback/:courseId";
export const RESOURCE_DETAIL = ACADEMY + "/resource/:resourceId";
export const VIEW_RECOMMENDATION_HISTORY =
  XRAY + "/recomm-initiative/history/:practiceId/:surveyId";

export const BUSINESS_IMPROVEMENT_PLAN = `${COACH_PANEL}/business-improvement-plan/:userId`;
export const BUSINESS_IMPROVEMENT_PLAN_VIEW = `${COACH_PANEL}/business-improvement-plan/:userId/view/:planId`;
// Old "Business Improvement Plans" (removed, OkrPlans/OKR_PLANS_COACH.md CD30).
// Kept only so old bookmarks redirect to OKR Plans.
export const LEGACY_BUSINESS_PLAN = `${COACH_PANEL}/business-plan`;
